import { useState, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { getAccounts } from '../services/accountService'
import { deleteCVU } from '../services/cvuService'
import { verifyPassword } from '../services/authService'
import { LuTrash2, LuTriangleAlert } from 'react-icons/lu'
import PasswordModal from '../components/PasswordModal'
import './BajaCVUPage.css'

function BajaCVUPage() {
  const { userRole } = useAuth()
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingAccounts, setLoadingAccounts] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [responseInfo, setResponseInfo] = useState(null)

  // Modal de confirmación
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [pendingData, setPendingData] = useState(null)
  const [verifying, setVerifying] = useState(false)

  // Form fields
  const [accountId, setAccountId] = useState('')
  const [cvu, setCvu] = useState('')
  const [cuit, setCuit] = useState('')

  useEffect(() => {
    loadAccounts()
  }, [])

  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true)
      const response = await getAccounts()
      const accountsList = response.data || []
      setAccounts(accountsList)
      if (accountsList.length > 0) {
        setAccountId(accountsList[0].id)
      }
    } catch (err) {
      setError('Error al cargar cuentas')
    } finally {
      setLoadingAccounts(false)
    }
  }

  const resetForm = () => {
    setCvu('')
    setCuit('')
    setError('')
    setSuccess('')
    setResponseInfo(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setResponseInfo(null)

    // Validaciones
    if (!accountId) {
      setError('Debe seleccionar una cuenta recaudadora')
      return
    }

    if (!cvu) {
      setError('CVU es requerido')
      return
    }

    if (!/^\d{22}$/.test(cvu)) {
      setError('CVU debe tener exactamente 22 dígitos numéricos')
      return
    }

    if (!cuit) {
      setError('CUIT es requerido')
      return
    }

    if (!/^\d{11}$/.test(cuit)) {
      setError('CUIT debe tener exactamente 11 dígitos numéricos (sin guiones)')
      return
    }

    const data = {
      account_id: accountId,
      cvu,
      cuit
    }

    // Guardar datos y mostrar modal de confirmación
    setPendingData(data)
    setShowPasswordModal(true)
  }

  const handlePasswordConfirm = async (password) => {
    setVerifying(true)
    
    try {
      // Re-autenticar usuario
      await verifyPassword(password)
      
      // Si el login es exitoso, cerrar modal y ejecutar operación
      setShowPasswordModal(false)
      
      // Ejecutar baja de CVU
      await executeDeleteCVU(pendingData)
      
      return { success: true }
    } catch (err) {
      console.error('Error de verificación:', err)
      return { 
        success: false, 
        error: err.response?.data?.error || 'Contraseña incorrecta'
      }
    } finally {
      setVerifying(false)
    }
  }

  const executeDeleteCVU = async (data) => {
    try {
      setLoading(true)
      const response = await deleteCVU(data)
      
      setSuccess(`CVU dado de baja exitosamente!`)
      
      // Mostrar respuesta completa
      if (response.httpCode && response.fullResponse) {
        setResponseInfo({
          code: response.httpCode,
          message: response.message || `HTTP ${response.httpCode}`,
          fullResponse: response.fullResponse
        })
      }
      
      // Resetear formulario después de éxito
      setTimeout(() => {
        resetForm()
      }, 1000)
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al dar de baja CVU'
      setError(errorMsg)
    } finally {
      setLoading(false)
      setPendingData(null)
    }
  }

  if (loadingAccounts) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Cargando cuentas</p>
      </div>
    )
  }

  // Solo mostrar para BIND
  if (userRole === 'CREDENCIAL') {
    return (
      <div className="page-container">
        <div className="disabled-feature">
          <div className="disabled-icon">⚠️</div>
          <h2>Funcionalidad No Disponible</h2>
          <p>Esta funcionalidad solo está disponible para usuarios BIND.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><LuTrash2 /> Baja de CVU</h1>
      </div>

      {/* Modal de confirmación de contraseña */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false)
          setPendingData(null)
        }}
        onConfirm={handlePasswordConfirm}
        title="Confirmar Baja de CVU"
        message="ATENCIÓN: Esta operación es irreversible. El CVU será eliminado permanentemente y no podrá volver a darse de alta para otro CUIT."
        loading={verifying}
      />

      {/* Warning */}
      <div className="warning-banner">
        <LuTriangleAlert className="warning-icon" />
        <div>
          <strong>Advertencia:</strong> La baja de CVU es una operación lógica e irreversible. 
          Una vez eliminado, no se podrá volver a dar de alta ese CVU para otro CUIT.
        </div>
      </div>

      {responseInfo && (
        <div className="response-info">
          <div className="response-header">
            <strong>{responseInfo.message}</strong>
            <button 
              className="btn-close-response" 
              onClick={() => setResponseInfo(null)}
              title="Cerrar"
            >
              ✕
            </button>
          </div>
          <details open>
            <summary>Respuesta completa de Bind API</summary>
            <pre>{responseInfo.fullResponse}</pre>
          </details>
        </div>
      )}

      <form onSubmit={handleSubmit} className="baja-cvu-form">
        <div className="form-section">
          <h3>Cuenta Recaudadora</h3>

          <div className="form-group">
            <label>Cuenta</label>
            <select 
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="form-control"
              required
            >
              {accounts.map(acc => (
                <option key={acc.id} value={acc.id}>
                  {acc.account_routing?.address || acc.cbu || acc.cvu || acc.number} - {acc.type}{acc.label ? ` - ${acc.label}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-section">
          <h3>Datos del CVU a Eliminar</h3>

          <div className="form-group">
            <label>CVU <span className="required">*</span></label>
            <input 
              type="text"
              value={cvu}
              onChange={(e) => setCvu(e.target.value.replace(/[^0-9]/g, ''))}
              className="form-control"
              placeholder="0000003380201901240010"
              maxLength="22"
              required
            />
            <small className="form-help">22 dígitos numéricos</small>
          </div>

          <div className="form-group">
            <label>CUIT del Cliente <span className="required">*</span></label>
            <input 
              type="text"
              value={cuit}
              onChange={(e) => setCuit(e.target.value.replace(/[^0-9]/g, ''))}
              className="form-control"
              placeholder="20123456789"
              maxLength="11"
              required
            />
            <small className="form-help">11 dígitos numéricos (sin guiones)</small>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="form-actions">
          <button 
            type="button" 
            onClick={resetForm}
            className="btn-secondary"
            disabled={loading}
          >
            Limpiar
          </button>
          <button 
            type="submit"
            className="btn-danger"
            disabled={loading}
          >
            <LuTrash2 /> {loading ? 'Procesando...' : 'Dar de Baja CVU'}
          </button>
        </div>
      </form>
    </div>
  )
}

export default BajaCVUPage

